<template>
  <div v-if="show" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div class="relative bg-white w-full h-full flex flex-col p-6 overflow-y-auto">
      <div class="text-right">
        <button @click="close" class="text-gray-500 font-bold font-sans text-lg hover:text-pink-600">X</button>
      </div>
      <div class="flex justify-center">
        <img src="@/assets/logo.svg" alt="Logo" class="h-18 w-44 mb-4" />
      </div>
      <div class="text-center mt-6 mb-4">
        <p class="text-lg font-bold text-green-600 mb-1">Select Guest Party</p>
        <p v-if="deal" class="font-bold text-md text-gray-800">{{ deal }}</p>
        <p class="text-md text-gray-700">{{ description }}</p>
        <p class="text-sm text-gray-500 mt-1">
          Voucher total: {{ totalAdultCount }} adults, {{ totalChildCount }} children across {{ guests.length }} {{ guests.length === 1 ? 'party' : 'parties' }}
        </p>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div
          v-for="guest in guests"
          :key="guest.id"
          class="flex items-center justify-between border-b py-3"
        >
          <div>
            <p class="font-semibold text-gray-800">{{ guest.guest_name }}</p>
            <p class="text-sm text-gray-500">
              {{ guest.adult_count }} adult{{ guest.adult_count === 1 ? '' : 's' }}<template v-if="guest.child_count > 0">, {{ guest.child_count }} child{{ guest.child_count === 1 ? '' : 'ren' }}</template>
            </p>
          </div>
          <div>
            <span v-if="guest.status === 'Redeemed'" class="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
              &#10003; Redeemed
            </span>
            <button
              v-else
              @click="checkIn(guest)"
              :disabled="redeemingId === guest.id"
              class="bg-button hover:bg-input text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {{ redeemingId === guest.id ? 'Checking in...' : 'Check In' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="showMessage" class="mt-4 px-4 py-2 rounded-lg" :class="{ 'bg-red-200': !isValid, 'bg-green-200': isValid }">
        <p class="font-normal">{{ message }}</p>
      </div>
    </div>

    <div v-if="pendingGuest" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg p-6 w-4/5 max-w-sm text-center">
        <p class="text-lg font-bold text-gray-800 mb-1">Check in guest?</p>
        <p class="text-md text-gray-700 mb-1">{{ pendingGuest.guest_name }}</p>
        <p class="text-sm text-gray-500 mb-4">{{ pendingPaxLabel }}</p>
        <p class="text-xs text-gray-400 mb-4">This cannot be undone.</p>
        <div class="flex justify-center space-x-2">
          <button @click="cancelCheckIn" class="px-4 py-2 rounded-lg text-sm bg-gray-200 hover:bg-gray-300 text-gray-700">
            Cancel
          </button>
          <button @click="confirmCheckIn" class="px-4 py-2 rounded-lg text-sm bg-button hover:bg-input text-white">
            Yes, Check In
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RepositoryFactory from '../../repositories/RepositoryFactory';

const Suppliers = RepositoryFactory.get('supplier');

export default {
  name: 'SelectGuestParty',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    voucherCode: {
      type: String,
      default: '',
    },
    guideID: {
      type: [String, Number],
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    deal: {
      type: String,
      default: '',
    },
    initialGuests: {
      type: Array,
      default: () => [],
    },
    totalAdultCount: {
      type: [String, Number],
      default: 0,
    },
    totalChildCount: {
      type: [String, Number],
      default: 0,
    },
  },
  data() {
    return {
      guests: [],
      redeemingId: null,
      showMessage: false,
      isValid: false,
      message: '',
      pendingGuest: null,
    };
  },
  computed: {
    pendingPaxLabel() {
      if (!this.pendingGuest) {
        return '';
      }
      const g = this.pendingGuest;
      return g.adult_count + ' adult' + (g.adult_count === 1 ? '' : 's')
        + (g.child_count > 0 ? ', ' + g.child_count + ' child' + (g.child_count === 1 ? '' : 'ren') : '');
    },
  },
  watch: {
    initialGuests: {
      immediate: true,
      handler(val) {
        this.guests = (val || []).map((g) => ({ ...g }));
      },
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    checkIn(guest) {
      this.pendingGuest = guest;
    },
    cancelCheckIn() {
      this.pendingGuest = null;
    },
    async confirmCheckIn() {
      const guest = this.pendingGuest;
      this.pendingGuest = null;
      if (!guest) {
        return;
      }

      this.redeemingId = guest.id;
      this.showMessage = false;
      try {
        const { data } = await Suppliers.redeemVoucherGuest(this.voucherCode, guest.id, this.guideID);
        const success = data.statuscode === process.env.VUE_APP_API_STATUS_SUCCESS;
        if (success) {
          const target = this.guests.find((g) => g.id === guest.id);
          if (target) {
            target.status = 'Redeemed';
          }
          if (data.output && data.output.voucher_fully_redeemed) {
            this.message = 'All guest parties have been checked in.';
            this.isValid = true;
            this.showMessage = true;
          }
        } else {
          this.message = data.msg;
          this.isValid = false;
          this.showMessage = true;
        }
      } catch (e) {
        this.message = 'Request failed, please try again.';
        this.isValid = false;
        this.showMessage = true;
      } finally {
        this.redeemingId = null;
        if (this.showMessage) {
          setTimeout(() => {
            this.showMessage = false;
            this.message = '';
          }, 4000);
        }
      }
    },
  },
};
</script>
